import type { ChimeWaitingItem } from "./chimeTelehealthService";
import { guestHandleMatches, type PresentChimeGuest } from "./chimeParticipantVisuals";

/**
 * One row in the provider's waiting-room list, whichever door the person came through. A guest
 * row with `participant` came through that participant's personal link and is shown by name.
 */
export type WaitingEntry =
  | { kind: "patient"; patientRef: string; label: string }
  | { kind: "guest"; guestId: string; label: string; participant?: string };

function toEntry(
  item: ChimeWaitingItem,
  labelForRef?: (ref: string) => string | undefined,
): WaitingEntry {
  if (item.kind === "patient") {
    return { kind: "patient", patientRef: item.id, label: labelForRef?.(item.id) ?? item.name };
  }
  if (item.participantRef) {
    return {
      kind: "guest",
      guestId: item.id,
      participant: item.participantRef,
      label: labelForRef?.(item.participantRef) ?? item.name,
    };
  }
  return { kind: "guest", guestId: item.id, label: `${item.name} (guest)` };
}

/**
 * The people waiting, from `session-status` (stale guests are already left out by the bot).
 * Patients first — the client is the expected joiner; guests are the exception.
 */
export function waitingEntries(
  items: readonly ChimeWaitingItem[] | undefined,
  chartLabelForPatientRef?: (patientRef: string) => string | undefined,
): WaitingEntry[] {
  const waiting = (items ?? []).filter((item) => !item.admitted);
  return [
    ...waiting.filter((item) => item.kind === "patient"),
    ...waiting.filter((item) => item.kind === "guest"),
  ].map((item) => toEntry(item, chartLabelForPatientRef));
}

/** Full handles of the guests in a `session-status` waiting list (waiting or admitted). */
export function guestHandles(items: readonly ChimeWaitingItem[] | undefined): string[] {
  return (items ?? []).filter((item) => item.kind === "guest").map((item) => item.id);
}

/** A guest in the call with no waiting row. `handle` is what Remove sends: the full handle when known, else the prefix. */
export type UnlistedRosterGuest = { handlePrefix: string; name: string; handle: string };

/**
 * Guests in the Chime roster with no admitted row (in the call already, or a failed or racing removal). The roster
 * carries only a 12-hex handle prefix (Decision 39); `remove-guest` accepts it, so Remove works even after the provider
 * reloads. The full handle is used when this page saw the guest's row (`knownHandles`).
 */
export function unlistedRosterGuests(
  present: readonly PresentChimeGuest[],
  admittedHandles: readonly string[],
  knownHandles: readonly string[],
): UnlistedRosterGuest[] {
  return present
    .filter((guest) => !admittedHandles.some((handle) => guestHandleMatches(handle, guest.handlePrefix)))
    .map((guest) => ({
      ...guest,
      handle: knownHandles.find((h) => guestHandleMatches(h, guest.handlePrefix)) ?? guest.handlePrefix,
    }));
}

/**
 * Guests admitted (in the call or not yet), so the provider can still remove one. Labelled with the
 * name the guest gave, not the chart name of a client they were matched to.
 */
export function admittedGuestEntries(
  items: readonly ChimeWaitingItem[] | undefined,
): (WaitingEntry & { kind: "guest" })[] {
  return (items ?? [])
    .filter((item) => item.kind === "guest" && item.admitted)
    .map((item) => ({
      kind: "guest" as const,
      guestId: item.id,
      label: item.name,
      ...(item.participantRef ? { participant: item.participantRef } : {}),
    }));
}

export function waitingBannerText(waitingCount: number): string | undefined {
  if (waitingCount <= 0) return undefined;
  if (waitingCount === 1) return "Someone is waiting to join.";
  return `${waitingCount} people are waiting to join.`;
}
